import { IShiftCalendarUtil } from "Interfaces/Utils/IShiftCalendarUtil";
import { injectable } from "inversify";
import { Shift } from "Models/Shifts/Shift";
import { ShiftCalendarItem } from "Models/Shifts/ShiftCalendarItem";
import { TaskOneTimeRecurrency } from "Models/Tasks/TaskOneTimeRecurrency";
import { TaskRecurrency } from "Models/Tasks/TaskRecurrencies/TaskRecurrency";
import { TaskShiftRecurrency } from "Models/Tasks/TaskRecurrencies/TaskShiftRecurrency";
import moment from "moment";
import i18n from "Translations/i18n";
import { ShiftCalendarMode } from "../ShiftCalendarMode";

@injectable()
export class ShiftCalendarUtil implements IShiftCalendarUtil {

    // The week starts at sunday in the old CMS
    private daysOfWeek: number[] = [1, 2, 3, 4, 5, 6, 0];

    public convertShifts(shifts: Shift[], shiftCalendarMode: ShiftCalendarMode, date?: Date): ShiftCalendarItem[] {
        switch (shiftCalendarMode) {
            case ShiftCalendarMode.ONLY_ONCE:
                return this.getConvertedOnlyOnceShifts(shifts, date);
            case ShiftCalendarMode.SHIFTS:
                return this.getConvertedShifts(shifts);
            case ShiftCalendarMode.DAILY_WEEKLY:
                return this.getConvertedDailyShifts();
        }

        return [];
    }

    private getConvertedOnlyOnceShifts(shifts: Shift[], date?: Date): ShiftCalendarItem[] {
        const convertedShifts: ShiftCalendarItem[] = [];

        if (date === undefined) {
            return [];
        }

        const momentDate = moment(date);
        // In momentjs sunday is 0, monday is 1, etc.
        // We need monday to be 0 so we subtract 1
        let dayIndex = momentDate.day() - 1;
        // If the index is below 0 (sunday - 1) we set it to 6
        if (dayIndex < 0) {
            dayIndex = 6;
        }

        shifts.forEach(shift => {
            if (shift.weekday === dayIndex) {
                const time = `${shift.start} - ${shift.end}`;
                const newShiftCalendarItem = new ShiftCalendarItem(time, [shift.id]);
                convertedShifts.push(newShiftCalendarItem);
            }
        });

        return convertedShifts;
    }

    private getConvertedShifts(shifts: Shift[]): ShiftCalendarItem[] {
        const convertedShifts: ShiftCalendarItem[] = [];
        const defaultDays: any = [undefined, undefined, undefined, undefined, undefined, undefined, undefined];

        shifts.forEach(shift => {
            const time = `${shift.start} - ${shift.end}`;
            const shiftCalendarItem = convertedShifts.find(convertedShift => {
                return convertedShift.time === time;
            });

            if (shiftCalendarItem) {
                shiftCalendarItem.days[shift.weekday] = shift.id;
            }
            else {
                const newShiftCalendarItem = new ShiftCalendarItem(time, [...defaultDays]);
                newShiftCalendarItem.days[shift.weekday] = shift.id;
                convertedShifts.push(newShiftCalendarItem);
            }
        });

        return convertedShifts;
    }

    private getConvertedDailyShifts(): ShiftCalendarItem[] {
        const defaultDays: any = [true, true, true, true, true, true, true];
        const newShiftCalendarItem = new ShiftCalendarItem("", [...defaultDays]);
        return [newShiftCalendarItem];
    }

    public getWeekDays(shiftCalendarMode: ShiftCalendarMode): string[] {
        const days: string[] = [];

        if (shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE) {
            days.push(i18n.t("shiftCalendar.weekdaysOnlyOnce"));
        }
        else if (shiftCalendarMode === ShiftCalendarMode.SHIFTS) {
            for (let i = 0; i < 7; ++i) {
                days.push(moment(i, "e").startOf("week").isoWeekday(i + 1).format("ddd"));
            }
        }
        else if (shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
            for (let i = 0; i < 7; ++i) {
                days.push(moment(i, "e").startOf("week").isoWeekday(i + 1).format("dddd"));
            }
        }

        return days;
    }

    public addRecurrence(taskRecurrency: TaskRecurrency, index: number, shiftId: number, shiftCalendarMode: ShiftCalendarMode) {
        const recurrencies: TaskOneTimeRecurrency[] | TaskShiftRecurrency[] | undefined =
            shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE ? taskRecurrency.taskOneTimeRecurrencies
                : shiftCalendarMode === ShiftCalendarMode.SHIFTS ? taskRecurrency.taskShiftRecurrencies
                    : undefined;

        if (recurrencies !== undefined) {
            if (shiftId && !recurrencies.find(taskOneTimeRecurrence => taskOneTimeRecurrence.shiftId === shiftId)) {
                recurrencies.push({ taskRecurrencyId: taskRecurrency.id, shiftId });
            }
        }
        else if (shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
            taskRecurrency["weekday" + this.daysOfWeek[index]] = true;
        }
    }

    public removeRecurrence(taskRecurrency: TaskRecurrency, index: number, shiftId: number, shiftCalendarMode: ShiftCalendarMode): boolean {
        const recurrencies: TaskOneTimeRecurrency[] | TaskShiftRecurrency[] | undefined =
            shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE ? taskRecurrency.taskOneTimeRecurrencies
                : shiftCalendarMode === ShiftCalendarMode.SHIFTS ? taskRecurrency.taskShiftRecurrencies
                    : undefined;

        if (recurrencies !== undefined) {
            for (let i = recurrencies.length - 1; i >= 0; --i) {
                if (recurrencies[i].shiftId === shiftId) {
                    recurrencies.splice(i, 1);
                    return true;
                }
            }

            // If shiftId is undefined we return true
            return shiftId === undefined;
        }

        // if ShiftCalendarMode is neither ONLY_ONCE or SHIFTS, it is DAILY_WEEKLY, so it will be treated as such
        if (taskRecurrency["weekday" + this.daysOfWeek[index]]) {
            taskRecurrency["weekday" + this.daysOfWeek[index]] = false;
            return true;
        }

        return false;
    }

    public selectAllDays(taskRecurrency: TaskRecurrency, convertedShift: ShiftCalendarItem, shiftCalendarMode: ShiftCalendarMode) {
        let removedAll = true;

        if (shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE || shiftCalendarMode === ShiftCalendarMode.SHIFTS) {
            for (let i = 0; i < convertedShift.days.length; ++i) {
                if (!this.removeRecurrence(taskRecurrency, i, convertedShift.days[i], shiftCalendarMode)) {
                    removedAll = false;
                    break;
                }
            }

            if (!removedAll) {
                for (let i = 0; i < convertedShift.days.length; ++i) {
                    this.addRecurrence(taskRecurrency, i, convertedShift.days[i], shiftCalendarMode);
                }
            }
        }
    }

    public selectAllTimes(taskRecurrency: TaskRecurrency, convertedShifts: ShiftCalendarItem[], dayIndex: number, shiftCalendarMode: ShiftCalendarMode) {
        if (shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE || shiftCalendarMode === ShiftCalendarMode.SHIFTS) {
            let removedAll = true;
            for (let i = 0; i < convertedShifts.length; ++i) {
                if (!this.removeRecurrence(taskRecurrency, i, convertedShifts[i].days[dayIndex], shiftCalendarMode)) {
                    removedAll = false;
                    break;
                }
            }

            if (!removedAll) {
                for (let i = 0; i < convertedShifts.length; ++i) {
                    this.addRecurrence(taskRecurrency, i, convertedShifts[i].days[dayIndex], shiftCalendarMode);
                }
            }
        }
        else if (shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
            taskRecurrency["weekday" + this.daysOfWeek[dayIndex]] = !taskRecurrency["weekday" + this.daysOfWeek[dayIndex]];
        }
    }

    public dayBoxIsSelected(taskRecurrency: TaskRecurrency, index: number, shiftId: number, shiftCalendarMode: ShiftCalendarMode): boolean {
        const array: TaskOneTimeRecurrency[] | TaskShiftRecurrency[] | undefined =
            shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE ? taskRecurrency.taskOneTimeRecurrencies
                : shiftCalendarMode === ShiftCalendarMode.SHIFTS ? taskRecurrency.taskShiftRecurrencies
                    : undefined;

        if (array !== undefined) {
            return !!array.find(recurrency => {
                return recurrency.shiftId === shiftId;
            });
        }
        else if (shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
            return taskRecurrency["weekday" + this.daysOfWeek[index]];
        }

        return false;
    }

    public selectAll(taskRecurrency: TaskRecurrency, convertedShifts: ShiftCalendarItem[], shiftCalendarMode: ShiftCalendarMode) {
        if (shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE || shiftCalendarMode === ShiftCalendarMode.SHIFTS) {
            for (const convertedShift of convertedShifts) {
                for (const day of convertedShift.days) {
                    this.addRecurrence(taskRecurrency, convertedShifts.indexOf(convertedShift), day, shiftCalendarMode);
                }
            }
        }
        else if (shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
            for (let i = 0; i < 7; ++i) {
                taskRecurrency["weekday" + i] = true;
            }
        }
    }

    public clearAll(taskRecurrency: TaskRecurrency, shiftCalendarMode: ShiftCalendarMode) {
        if (shiftCalendarMode === ShiftCalendarMode.ONLY_ONCE) {
            taskRecurrency.taskOneTimeRecurrencies.splice(0);
        }
        else if (shiftCalendarMode === ShiftCalendarMode.SHIFTS) {
            taskRecurrency.taskShiftRecurrencies.splice(0);
        }
        else if (shiftCalendarMode === ShiftCalendarMode.DAILY_WEEKLY) {
            for (let i = 0; i < 7; ++i) {
                taskRecurrency["weekday" + i] = false;
            }
        }
    }
}


// WEBPACK FOOTER //
// ./src/Components/ShiftCalendar/Utils/ShiftCalendarUtil.ts